import { equals, omit, pipe } from "rambda"

import { IS_PROD } from "../config/constants"
import { MOCK_CANDIDATES } from "./image-cache/protected-json-cache"

const LOCAL_STORAGE_KEY = "bookmarks"

interface AllBookmarks {
  gutenberg: {
    de: Record<string, string>
    fr: Record<string, string>
  }
  randomSentenceCandidates: string[]
  shortSentences: string[][]
}
const DEFAULT_BOOKMARKS = {
  gutenberg: {
    de: {},
    fr: {},
  },
  randomSentenceCandidates: [],
  shortSentences: [],
}

class Bookmarks {
  candidates: {
    add: (candidate: string)=> void
    getAll: ()=> string[]
    includes: (candidate: string)=> boolean
    remove: (candidate: string)=> void
    set: (candidates: string[])=> void
    toggleState: (candidate: string)=> void
  }
  gutenberg: {
    isBookmarked: (
      translationKey: string,
      language: LanguageWithBookmarks,
    )=> boolean
    toggleState: (
      word: TranslatedWord,
      language: LanguageWithBookmarks,
    )=> { isAdded: boolean }
  }
  shortSentences: {
    add: (shortSentence: string[])=> void
    remove: (shortSentence: string[])=> void
  }
  private bookmarks: AllBookmarks = DEFAULT_BOOKMARKS
  constructor() {
    const initialStateInStorage = localStorage.getItem(LOCAL_STORAGE_KEY)

    const initialState = initialStateInStorage
      ? JSON.parse(initialStateInStorage)
      : DEFAULT_BOOKMARKS
    this.bookmarks = {
      gutenberg: initialState.gutenberg ?? {},
      randomSentenceCandidates: initialState.randomSentenceCandidates ?? [],
      shortSentences: initialState.shortSentences ?? [],
    }
    this.gutenberg = {
      isBookmarked: (translationKey: string, language: LanguageWithBookmarks)=>
        !!this.bookmarks.gutenberg?.[language]?.[translationKey],
      toggleState: (word: TranslatedWord, language: LanguageWithBookmarks)=> {
        if (this.bookmarks.gutenberg?.[language]?.[word.translationKey]) {
          const newGutenberg = pipe(
            this.bookmarks.gutenberg[language],
            omit(word.translationKey),
          )

          this.bookmarks.gutenberg = {
            ...this.bookmarks.gutenberg,
            [language]: newGutenberg,
          }
          this.persistState()

          return { isAdded: false }
        } else {
          this.bookmarks.gutenberg = {
            ...this.bookmarks.gutenberg,
            [language]: {
              ...this.bookmarks.gutenberg[language],
              [word.translationKey]: word.translation,
            },
          }
          this.persistState()
          return { isAdded: true }
        }
      },
    }
    this.candidates = {
      add: (candidate: string)=> {
        this.bookmarks.randomSentenceCandidates.push(candidate)
        this.persistState()
      },
      getAll: ()=>
        IS_PROD ? this.bookmarks.randomSentenceCandidates : MOCK_CANDIDATES,
      includes: (candidate: string)=>
        this.bookmarks.randomSentenceCandidates.includes(candidate),
      remove: (candidate: string)=> {
        this.bookmarks.randomSentenceCandidates =
          this.bookmarks.randomSentenceCandidates.filter(x=> x !== candidate)
        this.persistState()
      },
      set: (candidates: string[])=> {
        this.bookmarks.randomSentenceCandidates = candidates
        this.persistState()
      },
      toggleState: (candidate: string)=> {
        if (this.candidates.includes(candidate)) {
          this.candidates.remove(candidate)
        } else {
          this.candidates.add(candidate)
        }
      },
    }
    this.shortSentences = {
      add: (shortSentence: string[])=> {
        this.bookmarks.shortSentences.push(shortSentence)
        this.persistState()
      },
      remove: (shortSentence: string[])=> {
        this.bookmarks.shortSentences = ShortSentence.filter(
          shortSentence,
          this.bookmarks.shortSentences,
        )
        this.persistState()
      },
    }
  }
  static convertBeforeExport(bookmarks: Map<string, string>) {
    return Array.from(bookmarks.entries()).map(
      ([translationKey, translation])=> ({
        translation,
        word: translationKey,
      }),
    )
  }

  addToShortSentences(shortSentence: string[]) {
    if (ShortSentence.includes(shortSentence, this.bookmarks.shortSentences)) {
      this.bookmarks.shortSentences.push(shortSentence)
    }
    this.persistState()
  }

  exportFile() {
    const a = document.createElement("a")
    a.href = `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(this.getExported()))}`
    a.download = "bookmarks.json"
    a.click()
    a.remove()
  }

  getExported() {
    return pipe(this.bookmarks, omit("randomSentenceCandidates"))
  }

  persistState() {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.bookmarks))
  }

  removeFromShortSentences(shortSentence: string[]) {
    this.bookmarks.shortSentences = ShortSentence.filter(
      shortSentence,
      this.bookmarks.shortSentences,
    )
    this.persistState()
  }
}

export class ShortSentence {
  static areEqual(a: string[], b: string[]) {
    return equals(a)(b)
  }
  static filter(a: string[], allShortSentences: string[][]) {
    return allShortSentences.filter(x=> !equals(a)(x))
  }
  static includes(a: string[], allShortSentences: string[][]) {
    return !allShortSentences.every(x=> !equals(a)(x))
  }
}

export const bookmarks = new Bookmarks()
